import React from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faVolumeUp, 
  faPlay, 
  faPause, 
  faCheckCircle,
  faExclamationTriangle,
  faMale,
  faFemale,
  faClone
} from '@fortawesome/free-solid-svg-icons';
import { Badge, Spinner, Alert } from 'react-bootstrap';
import './VoiceSelectionModal.css';

const VoiceSelectionModal = ({ 
  show,
  onHide,
  onSelectVoice,
  voiceList = [],
  userVoices = [],
  isLoadingVoices = false,
  selectedVoice,
  previewingVoiceId,
  isPreviewPlaying,
  onPreviewVoice
}) => {
  // 按 tags 分组音色
  const groupedVoices = React.useMemo(() => {
    const groups = {
      user: userVoices  // 用户自定义音色单独一组，键名为 'user'
    };
    
    // 按 tag 分组系统音色
    voiceList.forEach(voice => {
      if (voice.is_public && voice.tags && voice.tags.length > 0) {
        // 使用第一个 tag 作为分组
        const primaryTag = voice.tags[0];
        if (!groups[primaryTag]) {
          groups[primaryTag] = [];
        }
        groups[primaryTag].push(voice);
      } else if (voice.is_public) {
        // 没有 tags 的音色放到"其他"分类
        if (!groups['其他']) {
          groups['其他'] = [];
        }
        groups['其他'].push(voice);
      }
    });
    
    return groups;
  }, [voiceList, userVoices]);

  // 获取所有可用的标签（用于标签页）
  const availableTags = React.useMemo(() => {
    const tags = new Set();
    voiceList.forEach(voice => {
      if (voice.is_public && voice.tags && voice.tags.length > 0) {
        tags.add(voice.tags[0]);
      }
    });
    // 如果有没有 tags 的音色，添加"其他"分类
    const hasOther = voiceList.some(v => v.is_public && (!v.tags || v.tags.length === 0));
    if (hasOther) {
      tags.add('其他');
    }
    return Array.from(tags);
  }, [voiceList]);

  // 默认选中"我的音色"（如果有），否则选中第一个系统音色标签
  const [activeTab, setActiveTab] = React.useState(() => {
    return userVoices.length > 0 ? 'user' : (availableTags[0] || 'user');
  });

  // 当 availableTags 变化时，确保 activeTab 有效
  React.useEffect(() => {
    // 如果当前标签不是 'user' 且不在 availableTags 中，切换到第一个有效标签
    if (activeTab !== 'user' && !availableTags.includes(activeTab) && availableTags.length > 0) {
      setActiveTab(availableTags[0]);
    }
  }, [availableTags, activeTab]);

  // 阻止模态框背景滚动
  React.useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  if (!show) return null;

  const handleSelectVoice = (voiceId) => {
    if (onSelectVoice) {
      onSelectVoice(voiceId);
    }
  };

  const handleClose = () => {
    if (onHide) {
      onHide();
    }
  };

  // 点击背景关闭
  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('VoiceSelectionModal-backdrop')) {
      handleClose();
    }
  };

  // 渲染音色卡片
  const renderVoiceCard = (voice) => (
    <div
      key={voice.id}
      className={`VoiceSelectionModal-voice-card ${selectedVoice === voice.id ? 'active' : ''}`}
      onClick={() => handleSelectVoice(voice.id)}
    >
      {/* 选中标记 */}
      {selectedVoice === voice.id && (
        <div className="VoiceSelectionModal-voice-check">
          <FontAwesomeIcon icon={faCheckCircle} />
        </div>
      )}
      
      {/* 音色图标 */}
      <div className={`VoiceSelectionModal-voice-icon ${voice.is_public === false ? 'custom' : ''}`}>
        <FontAwesomeIcon 
          icon={voice.is_public === false ? faClone : (voice.gender === 'male' ? faMale : faFemale)} 
          size="2x"
        />
      </div>

      {/* 音色名称 */}
      <div className="VoiceSelectionModal-voice-name">
        {voice.name}
      </div>

      {/* Tags */}
      {voice.tags && voice.tags.length > 0 && (
        <div className="VoiceSelectionModal-voice-tags">
          {voice.tags.slice(0, 2).map((t, idx) => (
            <Badge key={idx} bg="secondary" className="VoiceSelectionModal-voice-tag">
              {t}
            </Badge>
          ))}
        </div>
      )}

      {voice.voice_type_display && (
        <div className="VoiceSelectionModal-voice-tags">
          <Badge bg="success" className="VoiceSelectionModal-voice-tag">
            {voice.voice_type_display}
          </Badge>
          {voice.usage_count !== undefined && (
            <Badge bg="info" className="VoiceSelectionModal-voice-tag">
              使用{voice.usage_count}次
            </Badge>
          )}
          {voice.is_expired && (
            <Badge bg="danger" className="VoiceSelectionModal-voice-tag">
              已过期
            </Badge>
          )}
        </div>
      )}

      {/* 描述 */}
      <div className="VoiceSelectionModal-voice-desc">
        {voice.description}
      </div>

      {/* 试听按钮 */}
      {voice.audio_url && (
        <button
          className={`VoiceSelectionModal-play-btn ${previewingVoiceId === voice.id && isPreviewPlaying ? 'playing' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onPreviewVoice(voice, e);
          }}
        >
          <FontAwesomeIcon 
            icon={previewingVoiceId === voice.id && isPreviewPlaying ? faPause : faPlay} 
          />
          <span>{previewingVoiceId === voice.id && isPreviewPlaying ? '停止' : '试听'}</span>
        </button>
      )}
    </div>
  );

  // 渲染标签内容
  const renderTabContent = (tag) => {
    const voices = groupedVoices[tag] || [];
    
    if (voices.length === 0) {
      if (tag === 'user') {
        return (
          <div className="VoiceSelectionModal-empty">
            <FontAwesomeIcon icon={faClone} size="3x" />
            <p>暂无克隆音色</p>
            <p style={{ fontSize: '12px', color: '#6b7280' }}>前往「音色克隆」页面创建专属音色</p>
          </div>
        );
      }
      return (
        <div className="VoiceSelectionModal-empty">
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
          <p>暂无{tag}音色</p>
        </div>
      );
    }

    return (
      <div className="VoiceSelectionModal-voice-grid">
        {voices.map(renderVoiceCard)}
      </div>
    );
  };

  const modalContent = (
    <div className="VoiceSelectionModal-backdrop" onClick={handleBackdropClick}>
      <div className="VoiceSelectionModal-container" onClick={(e) => e.stopPropagation()}>
        {/* 标题栏 */}
        <div className="VoiceSelectionModal-header">
          <h3 className="VoiceSelectionModal-title">
            🎤 选择音色
          </h3>
          <button 
            className="VoiceSelectionModal-close"
            onClick={handleClose}
            title="关闭"
          >
            ✕
          </button>
        </div>

        {/* 主体内容 */}
        <div className="VoiceSelectionModal-body">
          {isLoadingVoices ? (
            <div className="VoiceSelectionModal-loading">
              <Spinner animation="border" variant="primary" />
              <p>加载音色列表中...</p>
            </div>
          ) : voiceList.length === 0 ? (
            <Alert variant="warning" className="VoiceSelectionModal-alert">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              暂无可用音色，请联系管理员添加音色
            </Alert>
          ) : (
            <>
              {/* 标签页导航 */}
              <div className="VoiceSelectionModal-tabs">
                {/* 用户音色标签 - 始终显示 */}
                <button
                  className={`VoiceSelectionModal-tab ${activeTab === 'user' ? 'VoiceSelectionModal-tab-active' : ''}`}
                  onClick={() => setActiveTab('user')}
                >
                  我的音色 {userVoices.length > 0 ? `(${userVoices.length})` : ''}
                </button>
                
                {/* 系统音色标签 */}
                {availableTags.map(tag => (
                  <button
                    key={tag}
                    className={`VoiceSelectionModal-tab ${activeTab === tag ? 'VoiceSelectionModal-tab-active' : ''}`}
                    onClick={() => setActiveTab(tag)}
                  >
                    {tag} ({groupedVoices[tag]?.length || 0})
                  </button>
                ))}
              </div>

              {/* 标签内容 */}
              <div className="VoiceSelectionModal-content">
                {renderTabContent(activeTab)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default VoiceSelectionModal;

